import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { Overlay } from '@angular/cdk/overlay';
import { Portal, TemplatePortal } from '@angular/cdk/portal';
import { CdkTree, FlatTreeControl } from '@angular/cdk/tree';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    inject,
    isDevMode,
    IterableDiffers,
    OnInit,
    Renderer2,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { IAppRequest } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { treeType } from '@app/core/libraries/Tree/core/tree.helper';
import { TokenStorage } from '@app/core/token.storage';
import { AppBreadcrumbsService } from '@app/layout/breadcrumbs/services/app-breadcrumbs.service';
import { NotificationsService } from '@app/modules/notifications/notifications.service';
import { AppDialogService } from '@components/dialog/services/app-dialog.service';
import { InterfaceLogin } from '@modules/login/interfaces/login.interface';
import { UserListDialogComponent } from '@modules/users/dialogs/user-list-dialog/component/user-list-dialog.component';
import { UserListService } from '@modules/users/services/user-list.service';
import { NotifierService } from '@services/notifier.service';
import { TuiDestroyService } from '@taiga-ui/cdk';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom, interval, Observable, of, throwError, timer } from 'rxjs';
import { map, share, switchMap, take, takeUntil } from 'rxjs/operators';
import { MenuEditDialogComponent } from '../dialogs/menu-edit-dialog/menu-edit-dialog.component';
import { ICommand } from '../interfaces/Command.interface';
import { IMenuItem } from '../interfaces/MenuItem.interface';
import { MenuItem } from '../models/MenuItem';
import { SideMenuService } from '../services/side-menu.service';

/** ID верхнего элемента дерева */
export const ROOT_PARENT_ID = 1;

/** Время задержки при перетаскивании */
export const WAIT_TIME_MS = 1000;

@Component({
    selector: 'app-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['../styles/side-menu.component.scss'],
    providers: [TuiDestroyService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('popUpName', [
            state(
                'show',
                style({
                    opacity: 1,
                }),
            ),
            state(
                'hide',
                style({
                    opacity: 0,
                }),
            ),
            transition('hide => show', [animate('0.4s ease-in-out')]),
            transition('show => hide', [animate('0.4s ease-in-out')]),
        ]),
        trigger('blendIn', [
            transition(':enter', [
                style({ height: 0, opacity: 0 }),
                animate('150ms', style({ height: '*' })),
                animate('300ms', style({ opacity: 1 })),
            ]),
            transition(':leave', [animate('50ms', style({ opacity: 0 })), animate('150ms', style({ height: 0 }))]),
        ]),
    ],
})
export class SideMenuComponent implements OnInit {
    readonly #dialogService = inject(AppDialogService);
    readonly #destroy$ = inject(TuiDestroyService);

    /** Шаблон для confirm блока */
    @ViewChild('deleteItemPopup') domPortalContent: TemplateRef<any>;

    /** Текущая дата */
    readonly currentDateTime$: Observable<Date> = timer(0, 1000).pipe(
        map(() => new Date()),
        share(),
    );
    /** Объект портала */
    public menuPortal: Portal<any> | null;
    /** Триггер суб-меню */
    public contextMenuTriggerRef: MatMenuTrigger;
    /** Класс для генерации контейнеров для шаблонов */
    readonly #viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
    /** Используется ли сенсорное устройство */
    public get isTouchDevice(): boolean {
        return this.deviceService.isMobile();
    }

    constructor(
        @Inject(TuiDestroyService) private _destroy$: TuiDestroyService,
        private readonly deviceService: DeviceDetectorService,
        public service: SideMenuService,
        public appService: AppService,
        public router: Router,
        public userService: UserListService,
        private token: TokenStorage,
        private _render: Renderer2,
        private iterableDiffers: IterableDiffers,
        public notificationsService: NotificationsService,
        public dialog: MatDialog,
        public spinner: NgxSpinnerService,
        public notifier: NotifierService,
        private _changeDetection: ChangeDetectorRef,
        public media: MediaObserver,
        private titleService: Title,
        private _overlay: Overlay,
        private readonly _breadcrumbsService: AppBreadcrumbsService,
    ) {
        if (appService?.accessMatrix?.menu) {
            const menuAccess = appService.accessMatrix.menu;
            this.accessMenu = {
                add: menuAccess.add === 1,
                edit: menuAccess.edit === 1,
                delete: menuAccess.delete === 1,
            };
        }
        if (appService?.accessMatrix?.fui_pages) {
            const menuPages = appService.accessMatrix.fui_pages;
            this.accessPages = {
                add: menuPages.add === 1,
                edit: menuPages.edit === 1,
                delete: menuPages.delete === 1,
            };
        }

        this.iconNotifTootip = this.appService.language_dictionary.main_no_notifications;

        this.service.rootMenuItem = new MenuItem({
            id: ROOT_PARENT_ID,
            type: 'root',
        });

        this.service.menuTreeFlattener = new MatTreeFlattener(
            (node: MenuItem) => {
                this._flatNodeMap.set(node.id, node);
                return node;
            },
            (node: MenuItem) => node.level,
            (node: MenuItem) => node.expandable,
            (node: MenuItem): Observable<MenuItem[]> => of(node.children),
        );

        this.service.menuTreeControl = new FlatTreeControl<MenuItem>(
            (node: MenuItem) => node.level,
            (node: MenuItem) => node.expandable,
        );

        this.service.menuDataSource = new MatTreeFlatDataSource(
            this.service.menuTreeControl,
            this.service.menuTreeFlattener,
            this.service.generateTree(this.appService.menu ?? [], ROOT_PARENT_ID, 0, this.service.rootMenuItem),
        );
        this._breadcrumbsService.setBreadcrumbs(this.service.setActiveMenuItem(this.appService.link));

        const data = this._breadcrumbsService.getItemByIndex(-1);
        this.appService.sideMenuTitle = data?.name ?? '';
        this.titleService.setTitle(data?.name ?? this.appService.getSettingsByKey('header_big_text'));

        // Проверка на флаг фиксированного меню
        this.isMenuOpen = this.appService.getSettingsByKey('is_fixed_sidenav');

        if (this.isMenuOpen) {
            this.labelSate = 'show';
        }

        if (
            this.appService?.accessMatrix &&
            this.appService.accessMatrix?.notification_all?.show === 1 &&
            this.appService.accessMatrix?.notification?.show === 1
        ) {
            this.notificationsService.notificationFlag = true;
        }

        // Запуск счетчика для проверки уведомлений
        if (this.appService.isAuth) {
            interval(50000)
                .pipe(takeUntil(this._destroy$))
                .subscribe(() => {
                    this.countNew();
                });
        }

        this.notificationsService.unreadCount$.pipe(takeUntil(this._destroy$)).subscribe((val) => {
            const count = Number(val);
            if (count < 1) {
                this.iconNotifTootip = this.appService.language_dictionary.main_no_notifications;
                this.countStr = '';
                return;
            }
            this.iconNotifTootip = this.appService.language_dictionary.all_notifications;
            if (count <= 99) {
                this.countStr = String(count);
                return;
            }
            this.countStr = '99+';
        });

        // Обработка команд, полученных из шины команд
        this.service.commandBus.pipe(takeUntil(this.#destroy$)).subscribe((x) => this.processCommand(x));
    }

    /** Флаг - совершается ли перемещение ноды */
    public isDrag: boolean = false;

    /** Список уведомлений */
    public shortList = [];

    /** Состояние фильтра уведомлений */
    public filterState = 'new';

    /** Флаг - имеются ли непрочитанные уведомления */
    public isUnreadNotifications = false;

    /** Флаг состояния меню */
    public isMenuOpen = false;
    /** Строка для всплывашки количество уведомлений */
    public countStr: string = '';

    /** Тултип для счетчика уведомлений */
    public iconNotifTootip = '';

    /** Список открытых узлов дерева */
    public expansionModel = new SelectionModel<number>(true);

    /** Глобальный доступ к операциям с меню */
    public accessMenu: { add: boolean; edit: boolean; delete: boolean } = { add: false, edit: false, delete: false };

    /** Глобальный доступ к операциям со страницами */
    public accessPages: { add: boolean; edit: boolean; delete: boolean } = { add: false, edit: false, delete: false };

    /** Состояние анимации исчезновения и появление названий узлов */
    public labelSate: string = 'hide';

    /** Таймер для раскрытия узла */
    public expandTimeout: any;

    /** Набор id узлов и узлов в дереве */
    private _flatNodeMap = new Map<number, MenuItem>();

    /** Ссылка на элемент дерева */
    @ViewChild('menuTree') public menuTreeRef: CdkTree<MenuItem>;

    /** Ссылка на блок с боковым меню */
    @ViewChild('sideMenu') public sideMenuRef: ElementRef;

    /** Коэффициент для элемента, когда возле него опустили взятый узел */
    private _dropC: number;

    /** Количестко уведомлений */
    notificationCount = 0;

    /** Запоминаем развёрнутые подменю между сворачиваниями и разворачиваниями боковой панели */
    private expandedNodes: MenuItem[] = [];

    public ngOnInit(): void {
        // Добавляем анимацию плавного открытия дерева, после его первой инициализации
        setTimeout(() => {
            if (this.sideMenuRef?.nativeElement) {
                this._render.addClass(this.sideMenuRef.nativeElement, 'transform-menu');
            }
        });
    }

    public hasChildren = (_: number, node: treeType) => !!node.children && node.children.length > 0;

    /** Закрываем боковое меню */
    public closeMenu(): void {
        this.isMenuOpen = false;
        this.expandedNodes = this.service.menuTreeControl.dataNodes.filter((node) =>
            this.service.menuTreeControl.isExpanded(node),
        );
        this.service.menuTreeControl.collapseAll();
        this.labelSate = 'hide';
    }

    /** Открываем боковое меню */
    public openMenu(): void {
        this.expandedNodes.forEach((node) => this.service.menuTreeControl.expand(node));
        this.expandedNodes = [];
        this.isMenuOpen = true;
        this.labelSate = 'show';
    }

    /**
     * События изменения меню
     * @param event - Событие наведения или ухода мыши с элемента
     * @param open - флаг открыть / не открыть
     */
    menuEvent(event: any, open: boolean) {
        if (open) {
            this.openMenu();
        } else {
            const element = event?.relatedTarget as HTMLElement;
            if (element?.classList && !element?.classList.contains('cdk-overlay-backdrop')) {
                this.closeMenu();
            }
            if (event.toElement === null) {
                this.closeMenu();
            }
        }
    }

    /**
     * Открытие диалога
     * @param id идентификатор записи
     * @param data данные записи
     * @param type - тип пункта меню
     */
    public openDialog(id: string, data: IMenuItem, type: string): void {
        const dialogRef = this.dialog.open(MenuEditDialogComponent, {
            disableClose: true,
            autoFocus: false,
            width: '700px',
            data: { id, data, type },
        });
        const keyDown$ = dialogRef
            .keydownEvents()
            .pipe(takeUntil(this.#destroy$))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    dialogRef.close({ save: false });
                }
            });
        dialogRef
            .afterClosed()
            .pipe(take(1), takeUntil(this.#destroy$))
            .subscribe((result) => {
                keyDown$.unsubscribe();
                if (result.save) {
                    if (Number(data.parent_id) === 1) {
                        this.refreshNode(this.service.rootMenuItem);
                    } else {
                        this.refreshNode(this._flatNodeMap.get(Number(data.parent_id)));
                    }
                }
            });
    }

    /**
     * Редактирование записи
     * @param id - id записи
     * @param parentId - id записи родителя
     * @param type - тип пункта меню
     * @param level - уровень на котором редактируем иконку
     */
    public openEdit(id: number, parentId: number = 0, type: string = '', level: number = 0): void {
        this.spinner.show();

        this.appService
            .getRegisters(['AllDataSource', 'roles', 'system_sections', 'all_registry'])
            .pipe(
                take(1),
                switchMap((result: IAppRequest) => {
                    if (!result || !result.data) {
                        return throwError('Invalid registers list.');
                    }
                    this.service.guides = result.data;
                    if (id) {
                        return this.service.getItem({ id, type }).pipe(take(1));
                    }

                    const data: { data: IMenuItem; is_error: boolean } = {
                        is_error: false,
                        data: { id: 0, type, level },
                    };

                    if (parentId) {
                        data.data.parent_id = parentId;
                    }

                    return of(data);
                }),
                map((result: IAppRequest) => {
                    if (!result || !result.data || result?.is_error) {
                        return throwError(result);
                    }
                    result.data.level = level;
                    return result.data;
                }),
                takeUntil(this.#destroy$),
            )
            .subscribe({
                next: (data) => {
                    this.spinner.hide();
                    this.openDialog(String(id), data, data?.type ?? type);
                },
                error: (err: unknown) => {
                    this.spinner.hide();
                    this.notifier.exception(err);
                },
            });
    }

    /**
     * Удаление элемента
     * @param node - пункт меню
     */
    public async deleteItem(node: MenuItem): Promise<void> {
        this.spinner.show();
        try {
            const source = this.service.remove({ id: node.id, type: node.type });
            const result = await firstValueFrom(source);

            if (!result || result?.is_error) {
                throw result;
            }

            this.notifier.success(this.appService.language_dictionary.deletion_was_successful);
            this.refreshNode(node.level === 0 ? this.service.rootMenuItem : node.parentNode);
        } catch (e) {
            this.notifier.exception(e);
        } finally {
            this.spinner.hide();
        }
    }

    public initMenuPortal(template: TemplateRef<any>) {
        this.menuPortal = new TemplatePortal(template, this.#viewContainerRef);
    }

    /**
     * Установить положение меню
     * @param isFixed - флаг для фиксации меню
     */
    public fixedMenu(isFixed: boolean): void {
        this.service.updateIsFixedSidenav(isFixed);
        this.service
            .setMenuView({ is_fixed: isFixed })
            .toPromise()
            .then((result: IAppRequest) => {
                if (result?.is_error) {
                    this.service.updateIsFixedSidenav(!isFixed);
                    this.notifier.notify('error', result.message);
                }
            })
            .catch(() => {
                this.service.updateIsFixedSidenav(!isFixed);
                this.notifier.notify('error', this.service.language_dictionary.system_no_connection_with_system);
            });
    }

    /** Событие начала перетаскивания */
    public dragStart(): void {
        this.isDrag = true;
    }

    /** Событие окончания перетаскивания */
    public dragEnd(): void {
        this.isDrag = false;
    }

    /** Ховер по узлу во время перетаскивания */
    public dragHover(node: MenuItem, event) {
        if (this.isDrag) {
            this._dropC = event.offsetY / 45;

            clearTimeout(this.expandTimeout);
            this.expandTimeout = setTimeout(() => {
                this.service.menuTreeControl.expand(node);
            }, WAIT_TIME_MS);
        }
    }

    /** Окончания ховера над узлом */
    public dragHoverEnd(): void {
        if (this.isDrag) {
            clearTimeout(this.expandTimeout);
        }
    }

    /**
     * Перезагрузка ветки дерева
     * @param node - ветка
     */
    public refreshNode(node: MenuItem): void {
        node?.showSpinner();
        this.service
            .getChildren({
                language: this.appService.currentLanguage,
                id: node.id,
            })
            .toPromise()
            .then((result: IAppRequest) => {
                node?.hideSpinner();
                if (result.is_error) {
                    this.notifier.notify('error', result.message);
                    return;
                }

                const data: IMenuItem[] = result.data ?? [];

                // Пропатчить appService.menu свежими элементами меню #58119
                const idsToRefresh = data.map((x) => x.id);
                const filtered = this.appService.menu.filter((x) => !idsToRefresh.includes(x.id));
                this.appService.menu = [...filtered, ...data];

                if (node.id === 1) {
                    this.service.menuDataSource.data = this.service.generateTree(
                        data,
                        ROOT_PARENT_ID,
                        0,
                        this.service.rootMenuItem,
                    );
                } else {
                    node.setChildren(this.service.generateTree(data, node.id, node.level + 1, node));
                    this.service.renderTree();
                }
                this.service.setActiveMenuItem(this.appService.link);
            })
            .catch(() => {
                node?.hideSpinner();
                this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
            });
    }

    /** Выход из системы */
    public signOut(): void {
        this.spinner.show();
        this.appService
            .logout()
            .toPromise()
            .then((result: InterfaceLogin) => {
                this.spinner.hide();
                if (result && result.is_error === false) {
                    this.token.signOut();
                    this.router.navigate(['/login'], { state: { redirect: '' } });
                } else {
                    this.notifier.notify('error', result.message);
                }
            })
            .catch(() => {
                this.spinner.hide();
                this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
            });
    }

    /** Редактирование записи */
    openEditSelf(): void {
        this.spinner.show();
        this.appService
            .getRegisters(['roles', 'subjects', 'directory_of_posts', 'users'])
            .pipe(
                take(1),
                map((result: IAppRequest) => {
                    if (!result || result.is_error) {
                        throw new Error(result.message);
                    }
                    return result.data;
                }),
                switchMap((data: any) => {
                    this.service.guides = Object.assign({}, data);
                    return this.userService.getItem({ id: this.appService.userData.id });
                }),
                map((result: IAppRequest) => {
                    if (!result || result.is_error) {
                        throw new Error(result.message);
                    }
                    return result.data;
                }),
                takeUntil(this._destroy$),
            )
            .subscribe({
                next: (data: any) => {
                    this.spinner.hide();
                    this.openUserDialog(this.appService.userData.id, data);
                },
                error: (err: unknown) => {
                    this.spinner.hide();
                    this.notifier.exception(err);
                },
            });
    }

    /**
     * Открытие диалога
     * @param id идентификатор записи
     * @param data данные записи
     */
    openUserDialog(id: string, data): void {
        const dialogRef = this.dialog.open(UserListDialogComponent, {
            disableClose: true,
            autoFocus: false,
            width: '900px',
            maxHeight: '85vh',
            data: {
                title: this.service.language_dictionary.system_editing_user,
                id,
                data,
                access: this.appService?.accessMatrix?.users,
                isEditYourself: this.appService?.accessMatrix?.users?.edit !== 1,
                guides: this.service.guides,
            },
        });
        const keyDown$ = dialogRef
            .keydownEvents()
            .pipe(takeUntil(this.#destroy$))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    dialogRef.close({ save: false });
                }
            });
        dialogRef
            .afterClosed()
            .pipe(take(1), takeUntil(this.#destroy$))
            .subscribe((result) => {
                keyDown$.unsubscribe();
                if (result.save) {
                    this.notifier.notify('success', this.service.language_dictionary.system_data_successfully_saved);
                    Object.keys(this.appService.userData).forEach((key) => {
                        this.appService.userData[key] = result.data[key];
                    });
                    this.appService.userData.initials =
                        this.appService.userData?.firstname[0] + this.appService.userData?.lastname[0];
                    this.appService.userData.photo = result?.data?.gallery?.data?.photo_round?.file_name;
                    this.token.saveToken(JSON.stringify(this.appService.userData));
                }
            });
    }

    /** Открыть видео-инструкцию */
    public openVideoGuide(url: string): void {
        window.open(url, '_blank');
    }

    /** Открыть руководство */
    public openGuide(url: string): void {
        window.open('api' + url, '_blank');
    }

    /** Получение количества новых уведомлений */
    private countNew(): void {
        this.notificationsService
            .getCount()
            .pipe(take(1), takeUntil(this.#destroy$))
            .subscribe({
                next: (result) => {
                    if (Number.isSafeInteger(result)) {
                        this.notificationsService.unreadCount$.next(Number(result));
                    }
                },
                error: (err: unknown) => {
                    if (isDevMode()) {
                        console.error(err);
                    }
                },
            });
    }

    /**
     * Обработка нажатия на элемент дерева меню
     * @param $event - данные события
     * @param node - узел дерева
     */
    public clickNode($event: MouseEvent, node: MenuItem): void {
        if (node.type === 'folder' || node.url === 'main/admin' || node.url === 'main/settings') {
            if (this.service.menuTreeControl.isExpanded(node)) {
                this.service.menuTreeControl.collapse(node);
            } else {
                this.service.menuTreeControl.expand(node);
            }
            return;
        }

        // Переходим по ссылке
        this.openUrl(node, $event.ctrlKey || $event.metaKey);
    }

    /**
     * Переход по ссылке пункта меню
     * @param node - данные узла дерева
     * @param isNewWindow - открыть в новом окне
     */
    public openUrl(node: MenuItem, isNewWindow: boolean): void {
        if (!node.url || node.url === '') {
            return;
        }

        let url = node.url;

        // Внешний урл всегда открываем в новом окне
        if (node.type === 'url') {
            window.open(url);
            return;
        }

        // Добавляем ИД пункта меню к текстовым страницам
        if (node.type === 'page') {
            url = url + '/' + node.id;
        }

        // Открыть в новом окне
        if (isNewWindow) {
            // Ссылка старого интерфейса
            if (!node.isAngular) {
                url = this.appService.settings.system_back_url + '/' + url;
            }

            // Если ссылка на периодический реестр
            const isPeriodRegistryLink =
                node.type === 'registry' && (url.includes('factor/input') || url.includes('factor/moderation'));
            if (isPeriodRegistryLink && node.show_type === 1) {
                const searchParams = new URLSearchParams();
                searchParams.append('action', 'get_specific_item');
                searchParams.append('date', node.scheduler_date);
                url += '?' + searchParams.toString();
            }

            this.appService.openLink(url);
            return;
        }

        // Ссылка нового интерфейса
        if (node.isAngular) {
            // Ссылка на реестр
            if (node.type === 'registry' && (url.includes('factor/input') || url.includes('factor/moderation'))) {
                const params = node.show_type === 1 ? { action: 'get_specific_item', date: node.scheduler_date } : {};
                this.router.navigate([url], { queryParams: params }).then(() => {
                    this.service.setActiveMenuItem(this.appService.link);
                });
            } else {
                this.router.navigate([url]).then();
            }
        } else {
            this.appService.openLink(this.appService.settings.system_back_url + '/' + url, '_self');
        }
    }

    /**
     * Событие сброса элемента в нужное место
     * @param event - данные события
     */
    public dropItem(event: any): void {
        let operation: string;

        const visibleNodes = this.visibleNodes();
        const nodeAtDest = visibleNodes[event.currentIndex];

        // Текущий перетаскиваемый узел
        const node: MenuItem = event.item.data;

        // Игнорируем события помещения вне дерева или под системные пункты меню
        if (!event.isPointerOverContainer || !nodeAtDest.draggable) {
            this._dropC = 0;
            this.notifier.notify('warning', this.appService.language_dictionary.menu_drop_system_disabled);
            return;
        }

        if (this._dropC > 0.5) {
            operation = 'before';
        } else {
            operation = 'after';
        }

        if (operation === 'after' && this.service.menuTreeControl.isExpanded(nodeAtDest)) {
            operation = 'inside';
        }

        if (nodeAtDest.level > 0) {
            if (
                node?.type === 'folder' &&
                (operation === 'inside' || (operation !== 'inside' && nodeAtDest.level > 1))
            ) {
                this.notifier.notify('warning', this.appService?.language_dictionary?.cannot_be_moved_inside);
                this._dropC = 0;
                return;
            }
        }

        if (node.level === 0) {
            const index = this.service.menuDataSource.data.findIndex((item) => item.id === node.id);
            this.service.menuDataSource.data.splice(index, 1);
        } else {
            node.parentNode.removeChild(node.id);
        }

        switch (operation) {
            case 'inside':
                const newNode = new MenuItem(
                    Object.assign(node, { level: nodeAtDest.level + 1, parentNode: nodeAtDest }),
                );
                if (newNode.children.length) {
                    newNode.children.forEach((item) => {
                        item.level = newNode.level + 1;
                        if (item.children.length) {
                            item.children.forEach((child) => {
                                child.level = item.level + 1;
                            });
                        }
                    });
                }
                nodeAtDest.children.splice(0, 0, newNode);
                break;
            case 'before': {
                if (nodeAtDest.parentNode.id !== node.parentNode.id) {
                    node.setLevel(nodeAtDest.level);
                }
                if (nodeAtDest.level === 0) {
                    const hoverIndex = this.service.menuDataSource.data.findIndex((item) => item.id === nodeAtDest.id);
                    this.service.menuDataSource.data.splice(hoverIndex, 0, node);
                } else {
                    const hoverParen: MenuItem = nodeAtDest.parentNode;
                    const hoverIndex = hoverParen.children.findIndex((item) => item.id === nodeAtDest.id);
                    hoverParen.children.splice(hoverIndex, 0, node);
                }
                break;
            }
            case 'after': {
                if (nodeAtDest.parentNode.id !== node.parentNode.id) {
                    node.setLevel(nodeAtDest.level);
                }
                if (nodeAtDest.level === 0) {
                    const hoverIndex = this.service.menuDataSource.data.findIndex((item) => item.id === nodeAtDest.id);
                    this.service.menuDataSource.data.splice(hoverIndex + 1, 0, node);
                } else {
                    const hoverParen: MenuItem = nodeAtDest.parentNode;
                    const hoverIndex = hoverParen.children.findIndex((item) => item.id === nodeAtDest.id);
                    hoverParen.children.splice(hoverIndex + 1, 0, node);
                }
                break;
            }
        }

        this.service.renderTree();

        let parent;

        if (operation === 'insert') {
            parent = nodeAtDest;
        } else if (nodeAtDest.level === 0) {
            parent = this.service.rootMenuItem;
        } else {
            parent = nodeAtDest.parentNode;
        }

        this.service
            .changeOrder({
                node_src: node.id,
                node_target: nodeAtDest.id,
                node_operation: operation,
            })
            .toPromise()
            .then((result: IAppRequest) => {
                if (!result.is_error) {
                    this.refreshNode(parent);
                }
            })
            .catch((e) => {
                if (isDevMode()) {
                    console.error(e);
                }

                this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
            })
            .finally(() => {
                this._dropC = 0;
            });
    }

    /** Получить список открытых узлов дерева */
    visibleNodes(): MenuItem[] {
        const result = [];

        // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
        function addExpandedChildren(node: MenuItem, expanded: number[]) {
            result.push(node);
            if (expanded.includes(node.id)) {
                node.children.map((child) => addExpandedChildren(child, expanded));
            }
        }
        this.service.menuDataSource.data.forEach((node) => {
            addExpandedChildren(node, this.expansionModel.selected);
            if (this.service.menuTreeControl.isExpanded(node)) {
                node.children.forEach((child) => {
                    addExpandedChildren(child, this.expansionModel.selected);
                    if (this.service.menuTreeControl.isExpanded(child)) {
                        child.children.forEach((child2) => {
                            addExpandedChildren(child2, this.expansionModel.selected);
                        });
                    }
                });
            }
        });
        return result;
    }

    /** Проверка отображения кнопки меню */
    public showMenuNode(node: MenuItem): boolean {
        return this.showMenuAdd(node) || this.showMenuEdit(node) || this.showMenuDelete(node);
    }

    /** Проверка отображения кнопки меню Добавить */
    public showMenuAdd(node: MenuItem): boolean {
        return node?.isAdd && (this.accessMenu.add || this.accessPages.add);
    }

    /** Проверка отображения кнопки меню Редактировать */
    public showMenuEdit(node: MenuItem): boolean {
        return node?.isEdit && (this.accessMenu.edit || this.accessPages.edit);
    }

    /** Проверка отображения кнопки меню Удалить */
    public showMenuDelete(node: MenuItem): boolean {
        return node?.isRemove && (this.accessMenu.delete || this.accessPages.delete);
    }

    /**
     * Получение иконки для элемента
     * @param node - данные элемента
     */
    public getNodeIcon(node: MenuItem): string {
        if (node?.icon) {
            return node?.icon;
        }
        if (node.type === 'folder') {
            return 'folder';
        }
        return 'folder';
    }

    /**
     * Проверка на отображение единственной кнопки
     * @param node - данные элемента
     */
    public showOneButton(node: MenuItem) {
        return (
            [this.showMenuAdd(node), this.showMenuEdit(node), this.showMenuDelete(node)].filter((x) => x).length === 1
        );
    }

    /** Закрыть портал confirm блока */
    public closeConfirmDeletePortal(): void {
        setTimeout(() => {
            this.menuPortal?.detach();
            this.menuPortal = null;
        });
    }

    /** Закрыть confirm блок */
    public closeConfirmBlock(): void {
        this.contextMenuTriggerRef.closeMenu();
        this.closeConfirmDeletePortal();
        if (!this.appService.settings?.is_fixed_sidenav) {
            this.closeMenu();
        }
    }

    /** Обработка удалить на confirm блоке */
    public async confirmDel(): Promise<void> {
        const node: MenuItem = this.contextMenuTriggerRef.menuData?.item;
        if (!node) {
            return;
        }

        this.closeConfirmBlock();
        await this.processDelete(node);
    }

    async deleteWithConfirm(node: MenuItem) {
        const source = this.#dialogService.confirm(
            this.appService.language_dictionary.system_confirm_action,
            this.appService.language_dictionary.menu_confirm_message_delete,
        );
        const isConfirmed = await firstValueFrom(source);
        if (isConfirmed) {
            await this.processDelete(node);
        }
    }

    /** Удаление и дополнительные действия */
    public async processDelete(node: MenuItem) {
        await this.deleteItem(node);

        // Уходить со страницы после её удаления #58366
        if (this.appService.link.startsWith(node.url)) {
            this.router.navigate(['/']);
        }
    }

    public onNotifyListClose() {
        if (!this.appService.settings?.is_fixed_sidenav && this.isMenuOpen) {
            this.closeMenu();
        }
    }

    /** Обработка команд, полученных из шины команд */
    private processCommand(command: ICommand) {
        switch (command.name) {
            case 'delete': {
                const node = this.service.findNode((x) => x.id === command.args.id);
                if (node) {
                    this.processDelete(node);
                }
            }
        }
    }
}
